/**
 * Step 9: Gallery & Photos
 *
 * Collects hero image and gallery photos for the website.
 * Options: upload own images, use stock images, or skip.
 */

import { useState, useRef } from 'react';
import { ArrowLeft, ArrowRight, Upload, Image, ImageOff, X, Camera, Sparkles, Layout, Maximize, SplitSquareHorizontal, Minimize2, ChevronDown, MoveHorizontal } from 'lucide-react';
import type { Step9Data, ImageChoice, GalleryImage, HeroStyle, GalleryImageType } from '../../../types/onboarding';
import { HERO_STYLE_OPTIONS, GALLERY_IMAGE_TYPE_OPTIONS } from '../../../types/onboarding';

interface Step9Props {
  data: Step9Data | null;
  onChange: (data: Step9Data) => void;
  onNext: () => void;
  onBack: () => void;
  businessName: string;
  industry: string;
  saving: boolean;
  onUploadImage?: (file: File, slot: string) => Promise<string>;
}

// Image choice options for hero (no skip option - hero is required)
const HERO_IMAGE_CHOICES: Array<{ value: ImageChoice; label: string; description: string; icon: React.ElementType }> = [
  {
    value: 'upload',
    label: 'Upload My Own',
    description: 'Use your own photos for a personal touch',
    icon: Upload,
  },
  {
    value: 'stock',
    label: 'Use Stock Images',
    description: 'We\'ll select professional images for your industry',
    icon: Sparkles,
  },
];

// Image choice options for gallery (includes skip option)
const GALLERY_IMAGE_CHOICES: Array<{ value: ImageChoice; label: string; description: string; icon: React.ElementType }> = [
  {
    value: 'upload',
    label: 'Upload My Own',
    description: 'Use your own photos for a personal touch',
    icon: Upload,
  },
  {
    value: 'stock',
    label: 'Use Stock Images',
    description: 'We\'ll select professional images for your industry',
    icon: Sparkles,
  },
  {
    value: 'skip',
    label: 'Skip for Now',
    description: 'Add photos later from your dashboard',
    icon: ImageOff,
  },
];

// Gallery slot configuration
const GALLERY_SLOTS = [
  { id: 'gallery_1', label: 'Photo 1' },
  { id: 'gallery_2', label: 'Photo 2' },
  { id: 'gallery_3', label: 'Photo 3' },
  { id: 'gallery_4', label: 'Photo 4' },
  { id: 'gallery_5', label: 'Photo 5' },
  { id: 'gallery_6', label: 'Photo 6' },
];

// Map hero styles to icons
const HERO_STYLE_ICONS: Record<HeroStyle, React.ElementType> = {
  auto: Sparkles,                // Sparkles for "let niftee decide"
  split: SplitSquareHorizontal,
  'full-width': MoveHorizontal,  // Horizontal arrows (<- ->) for full-width
  immersive: Maximize,           // Maximize for full-screen
  compact: Minimize2,
};

export function Step9Gallery({
  data,
  onChange,
  onNext,
  onBack,
  businessName,
  industry,
  saving,
  onUploadImage,
}: Step9Props) {
  const [heroStyle, setHeroStyle] = useState<HeroStyle>(data?.hero_style || 'auto');
  const [heroChoice, setHeroChoice] = useState<ImageChoice>(data?.hero_image_choice || 'upload');
  const [heroImageUrl, setHeroImageUrl] = useState(data?.hero_image_url || '');
  const [galleryChoice, setGalleryChoice] = useState<ImageChoice>(data?.gallery_choice || 'upload');
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>(data?.gallery_images || []);
  const [uploading, setUploading] = useState<string | null>(null);

  const heroInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Save current state to parent
  const saveData = () => {
    onChange({
      hero_style: heroStyle,
      hero_image_choice: heroChoice,
      hero_image_url: heroImageUrl || undefined,
      gallery_choice: galleryChoice,
      gallery_images: galleryImages.filter(img => img.url),
    });
  };

  // Handle hero image upload
  const handleHeroUpload = async (file: File) => {
    if (!onUploadImage) return;

    setUploading('hero');
    try {
      const url = await onUploadImage(file, 'hero');
      setHeroImageUrl(url);
      saveData();
    } catch (error) {
      console.error('Error uploading hero image:', error);
    } finally {
      setUploading(null);
    }
  };

  // Handle gallery image upload
  const handleGalleryUpload = async (file: File, slot: string) => {
    if (!onUploadImage) return;

    setUploading(slot);
    try {
      const url = await onUploadImage(file, slot);
      const existingIndex = galleryImages.findIndex(img => img.slot === slot);

      if (existingIndex >= 0) {
        const updated = [...galleryImages];
        updated[existingIndex] = { ...updated[existingIndex], url };
        setGalleryImages(updated);
      } else {
        setGalleryImages([...galleryImages, { url, slot }]);
      }
      saveData();
    } catch (error) {
      console.error('Error uploading gallery image:', error);
    } finally {
      setUploading(null);
    }
  };

  // Remove gallery image
  const removeGalleryImage = (slot: string) => {
    setGalleryImages(galleryImages.filter(img => img.slot !== slot));
    saveData();
  };

  // Update gallery image type
  const updateGalleryImageType = (slot: string, image_type: GalleryImageType) => {
    const updated = galleryImages.map(img =>
      img.slot === slot ? { ...img, image_type } : img
    );
    setGalleryImages(updated);
  };

  // Update gallery image description
  const updateGalleryDescription = (slot: string, description: string) => {
    const updated = galleryImages.map(img =>
      img.slot === slot ? { ...img, description } : img
    );
    setGalleryImages(updated);
  };

  // Get gallery image by slot
  const getGalleryImage = (slot: string) => {
    return galleryImages.find(img => img.slot === slot);
  };

  // Can always proceed
  const canProceed = true;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Gallery & Photos
        </h1>
        <p className="text-gray-600">
          Great visuals make your website shine. Choose how you'd like to handle images.
        </p>
      </div>

      {/* Hero Style Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Layout className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Top of Homepage Layout</h2>
        </div>
        <p className="text-sm text-gray-500">
          Choose how the main image area at the top of your homepage looks. This is the first thing visitors see.
        </p>

        {/* Hero style selector - 3 on top row, 2 on bottom for visual balance */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {HERO_STYLE_OPTIONS.map((style) => {
            const Icon = HERO_STYLE_ICONS[style.value];
            return (
              <button
                key={style.value}
                onClick={() => {
                  setHeroStyle(style.value);
                  saveData();
                }}
                className={`
                  flex flex-col items-center p-4 rounded-xl border-2 transition-all text-center
                  ${heroStyle === style.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                  }
                `}
              >
                <Icon className={`w-8 h-8 mb-2 ${
                  heroStyle === style.value ? 'text-primary-600' : 'text-gray-400'
                }`} />
                <span className={`font-medium text-sm ${
                  heroStyle === style.value ? 'text-primary-900' : 'text-gray-900'
                }`}>
                  {style.label}
                </span>
                <span className="text-xs text-gray-500 mt-1 line-clamp-2">{style.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Main Image Up Top</h2>
        </div>
        <p className="text-sm text-gray-500">
          The big image at the top of your homepage. This sets the tone for your entire site.
        </p>

        {/* Hero choice selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {HERO_IMAGE_CHOICES.map((choice) => (
            <button
              key={choice.value}
              onClick={() => {
                setHeroChoice(choice.value);
                if (choice.value !== 'upload') {
                  setHeroImageUrl('');
                }
                saveData();
              }}
              className={`
                flex flex-col items-center p-4 rounded-xl border-2 transition-all text-center
                ${heroChoice === choice.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <choice.icon className={`w-8 h-8 mb-2 ${
                heroChoice === choice.value ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <span className={`font-medium ${
                heroChoice === choice.value ? 'text-primary-900' : 'text-gray-900'
              }`}>
                {choice.label}
              </span>
              <span className="text-xs text-gray-500 mt-1">{choice.description}</span>
            </button>
          ))}
        </div>

        {/* Hero upload area (only shown if 'upload' selected) */}
        {heroChoice === 'upload' && (
          <div className="mt-4">
            <input
              ref={heroInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleHeroUpload(file);
              }}
              className="hidden"
            />

            {heroImageUrl ? (
              <div className="relative aspect-[21/9] rounded-xl overflow-hidden border border-gray-200">
                <img
                  src={heroImageUrl}
                  alt="Hero image"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => heroInputRef.current?.click()}
                      className="px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => {
                        setHeroImageUrl('');
                        saveData();
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => heroInputRef.current?.click()}
                disabled={uploading === 'hero'}
                className="w-full aspect-[21/9] border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-400 hover:bg-primary-50/50 transition-colors flex flex-col items-center justify-center gap-3"
              >
                {uploading === 'hero' ? (
                  <>
                    <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-gray-500">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400" />
                    <div className="text-center">
                      <span className="text-primary-600 font-medium">Click to upload</span>
                      <p className="text-sm text-gray-500 mt-1">
                        Recommended: 1920x800px or larger
                      </p>
                    </div>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Stock image preview (only shown if 'stock' selected) */}
        {heroChoice === 'stock' && (
          <div className="mt-4 bg-gradient-to-br from-primary-100 to-purple-100 rounded-xl p-6 text-center">
            <Sparkles className="w-12 h-12 text-primary-500 mx-auto mb-3" />
            <p className="text-gray-700">
              We'll select a stunning hero image that matches {industry} businesses like {businessName}.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              You can always swap it out later from your dashboard.
            </p>
          </div>
        )}
      </div>

      {/* Gallery Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Photos</h2>
        </div>
        <p className="text-sm text-gray-500">
          Showcase your work, products, or space with up to 6 photos
        </p>

        {/* Gallery choice selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {GALLERY_IMAGE_CHOICES.map((choice) => (
            <button
              key={choice.value}
              onClick={() => {
                setGalleryChoice(choice.value);
                if (choice.value !== 'upload') {
                  setGalleryImages([]);
                }
                saveData();
              }}
              className={`
                flex flex-col items-center p-4 rounded-xl border-2 transition-all text-center
                ${galleryChoice === choice.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <choice.icon className={`w-8 h-8 mb-2 ${
                galleryChoice === choice.value ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <span className={`font-medium ${
                galleryChoice === choice.value ? 'text-primary-900' : 'text-gray-900'
              }`}>
                {choice.label}
              </span>
              <span className="text-xs text-gray-500 mt-1">{choice.description}</span>
            </button>
          ))}
        </div>

        {/* Gallery upload grid (only shown if 'upload' selected) */}
        {galleryChoice === 'upload' && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY_SLOTS.map((slot) => {
              const image = getGalleryImage(slot.id);
              const isUploading = uploading === slot.id;

              return (
                <div key={slot.id} className="space-y-2">
                  <input
                    ref={(el) => { galleryInputRefs.current[slot.id] = el; }}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleGalleryUpload(file, slot.id);
                    }}
                    className="hidden"
                  />

                  {image?.url ? (
                    <div className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group">
                      <img
                        src={image.url}
                        alt={slot.label}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <button
                          onClick={() => removeGalleryImage(slot.id)}
                          className="opacity-0 group-hover:opacity-100 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => galleryInputRefs.current[slot.id]?.click()}
                      disabled={isUploading}
                      className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-400 hover:bg-primary-50/50 transition-colors flex flex-col items-center justify-center gap-2"
                    >
                      {isUploading ? (
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span className="text-sm text-gray-500">{slot.label}</span>
                        </>
                      )}
                    </button>
                  )}

                  {image?.url && (
                    <div className="space-y-2">
                      {/* Image type dropdown */}
                      <div className="relative">
                        <select
                          value={image.image_type || ''}
                          onChange={(e) => {
                            updateGalleryImageType(slot.id, e.target.value as GalleryImageType);
                            saveData();
                          }}
                          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white pr-8"
                        >
                          <option value="">Select type...</option>
                          {GALLERY_IMAGE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                      {/* Description field */}
                      <input
                        type="text"
                        value={image.description || ''}
                        onChange={(e) => updateGalleryDescription(slot.id, e.target.value)}
                        onBlur={saveData}
                        placeholder="Brief description..."
                        maxLength={50}
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stock images preview */}
        {galleryChoice === 'stock' && (
          <div className="mt-4 bg-gradient-to-br from-primary-100 to-purple-100 rounded-xl p-6 text-center">
            <Sparkles className="w-12 h-12 text-primary-500 mx-auto mb-3" />
            <p className="text-gray-700">
              We'll curate a professional gallery of images perfect for {industry} businesses.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              You can replace any image later from your dashboard.
            </p>
          </div>
        )}
      </div>

      {/* Tip for better photos */}
      {(heroChoice === 'upload' || galleryChoice === 'upload') && (
        <div className="bg-blue-50 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">Tips for great photos</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Use high-resolution images (at least 1200px wide)</li>
            <li>• Good lighting makes a huge difference</li>
            <li>• Showcase your best work, products, or space</li>
            <li>• Horizontal photos work best for heroes and galleries</li>
          </ul>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={() => {
            saveData();
            onNext();
          }}
          disabled={!canProceed || saving || uploading !== null}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed && !uploading
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
